import { Injectable } from '@angular/core';
import { NgbModal }   from '@ng-bootstrap/ng-bootstrap';

import getCountryCodeAlpha3 from 'country-iso-2-to-3';

import { API }                             from '../api/api.constant';
import { ApiService }                      from '../api/api.service';
import { JsonService }                     from '../core/services/json.service';
import { SessionService }                  from '../sessions/session.service';
import { SyncXeroComponent }               from './xero.sync.component';
import { UserModel }                       from '../users/models/user';
import { XeroChartOfAccountsModel }        from './models/xero.chart-of-accounts';
import { XeroClientCredentials }           from './models/xero.client-credentials';
import { XeroCompanyModel }                from './models/xero.company';
import { XeroErrorComponent }              from './xero.error.component';
import { XeroOrganizationModel }           from './models/xero.organization';
import { XeroSettingsModel }               from './models/xero.settings';
import { XeroTaxRateModel }                from './models/xero.tax-rate';
import { XeroTokensModel }                 from './models/xero.tokens';
import { XeroTrackingCategories }          from './models/xero.tracking-categories';
import { XeroTrackingCategoryWithOptions } from './models/xero.tracking-categories.with-options';

@Injectable({
  providedIn: 'root'
})
export class XeroService {
  private _API       = API;
  public loginSsoUrl = 'https://edge.xero.com/platform/sso/xero-sso.js';
  public loginUrl    = 'https://login.xero.com/identity/connect/authorize';

  constructor (
    private apiService    : ApiService,
    private jsonService   : JsonService,
    private modalService  : NgbModal,
    private sessionService: SessionService
  ) {}

  accounts () : Promise<XeroChartOfAccountsModel> {
    return this.apiService.get('/xero/chart_of_accounts');
  }

  addXeroTax ( tax : string ) : Promise<any> {
    return this.apiService.post('/xero/adjust_tax_rates', tax, 'tax_rate');
  }

  checkExistingIntegrations () : Promise<string> {
    return this.apiService.get('/company/accounting_ids_exist/xero');
  }

  clearID () : Promise<boolean> {
    return this.apiService.post('/company/clear_accounting_contact_ids', {
      accounting_integration: 'xero',
      confirmation          : 'CLEAR IDS'
    });
  }

  didUserVerificationSucceed ( queryParams : any = this.jsonService.getQueryParameters() ) : boolean {
    return queryParams.xero_request_successful && queryParams.xero_request_successful === 'true';
  }

  disconnectUser () : Promise<boolean> {
    return this.apiService.get('/xero/disconnect_user');
  }

  getAuthenticationUrl () : string {
    return `${ this._API.url }/xero/new_from_sign_up`;
  }

  getClientCredentials () : Promise<XeroClientCredentials> {
    return this.apiService.get('/xero/get_xero_client_credentials');
  }

  getSettings () : Promise<XeroSettingsModel> {
    return this.apiService.get('/xero/settings', null, null, {
      timeout: 30000
    });
  }

  getSignupTokens ( queryParams : any = this.jsonService.getQueryParameters() ) : Promise<XeroTokensModel> {
    return this.apiService.get('/xero/exchange_code_for_tokens', null, null, {
      params: queryParams
    });
  }

  getUserForSignup ( tokens : XeroTokensModel ) : Promise<UserModel> {
    return this.apiService.post('/xero/organisation', {
      access_token: tokens.access_token
    })
    .then(( organization : XeroOrganizationModel ) => {
      return {
        company_name: organization.Name,
        country     : getCountryCodeAlpha3(organization.CountryCode).toLowerCase()
      };
    });
  }

  getSignupUrlFromCountry ( country : string, queryParams : any = this.jsonService.getQueryParameters() ) : Promise<string> {
    const tag = '/login.html#/xero-signup';

    queryParams.country = country;

    const queryString = Object.keys(queryParams)
    .map(( key : string ) => `${ key }=${ encodeURIComponent(queryParams[key]) }`)
    .join('&');

    switch (country) {
      case 'can':
        return Promise.resolve(`${ this._API.domains.can }${ tag }?${queryString}`);
      case 'gbr':
        return Promise.resolve(`${ this._API.domains.gbr }${ tag }?${queryString}`);
      case 'usa':
        return Promise.resolve(`${ this._API.domains.usa }${ tag }?${queryString}`);
      default:
        return Promise.resolve(`${ this._API.domains.aus }${ tag }?${queryString}`);
    }
  }

  getTaxRates () : Promise<Array<XeroTaxRateModel>> {
    return this.getSettings()
    .then(( settings : XeroSettingsModel ) => ( settings.tax_rates instanceof Array )
      ? settings.tax_rates
      : []
    );
  }

  getTrackingCategories () : Promise<Array<XeroTrackingCategories>> {
    return this.apiService.get('/xero/get_tracking_categories');
  }

  getTrackingCategoryWithOptions () : Promise<XeroTrackingCategoryWithOptions> {
    return this.apiService.get('/xero/get_tracking_options');
  }
  getVerifyUserUrl () : string {
    return `${ this._API.url }/xero/new/user_validation`;
  }

  grantAccess ( company : XeroCompanyModel, tokens : XeroTokensModel ) : Promise<string> {
    const params : any = { ...company, ...tokens };

    return this.apiService.post('/xero/grant_access_from_sign_up', params)
    .then(( result : any ) => result.login_code);
  }

  isNewUser ( queryParams : any = this.jsonService.getQueryParameters() ) : boolean {
    return !!queryParams.new_user;
  }

  isUserVerified ( user : UserModel ) : boolean {
    return !!user.xero_id;
  }

  login ( company : XeroCompanyModel, code : string ) : Promise<string> {
    return this.apiService.post('/sessions/xero_from_sign_up', {
      company_id: company.company_id,
      login_code: code
    })
    .then(( session : any ) => this.sessionService.setSessionAndValidate(session))
    .then(( rights : any ) => Promise.resolve('/workshop.html#/xero_settings?new_user'));
  }

  needsAuthentication ( queryParams : any = this.jsonService.getQueryParameters() ) : boolean {
    return !queryParams.code;
  }

  shareAccount ( shareAccountFlag : boolean ) : Promise<any> {
    return this.apiService.get('/company/share_accounting', [ shareAccountFlag ? 't' : 'f' ]);
  }

  showVerificationError ( queryParams : any = this.jsonService.getQueryParameters() ) : Promise<any> {
    return this.apiService.get('/xero/retrieve_error', queryParams.code)
    .then(( response : any ) => {
      const modal = this.modalService.open(XeroErrorComponent);

      modal.componentInstance.error = response.error_text;

      return modal.result;
    });
  }

  signup ( user : UserModel ) : Promise<XeroCompanyModel> {
    return this.apiService.post('/subscriptions/xero_setup', user);
  }

  sync () : Promise<number> {
    return this.modalService.open(SyncXeroComponent).result
    .then(( conflicts : string ) => parseInt(conflicts, 10));
  }

  wasUserVerificationAttempted ( queryParams : any = this.jsonService.getQueryParameters() ) : boolean {
    return !!queryParams.xero_request_successful;
  }
}
